// Curriculum standards + learning-module catalog for the Homeschool
// Learning Plan Builder. Deliberately compact — one source of truth
// consumed by the wizard, the weekly plan generator, and the parent
// dashboard tags. Extend the CURRICULA and MODULE_LIBRARY constants
// to broaden coverage; every downstream feature reads from here.

/** All curricula surfaced in the onboarding dropdown.
 *
 * Each entry now carries a `flag`, plain-language `short` label (age range +
 * region for non-expert parents), and a one-line `description` so people
 * can pick without needing to know what "KS2" or "PYP" means. The `id`
 * remains stable — the syllabus engine and Term Report both key off it.
 */
export const CURRICULA = [
  { id: "eyfs",      label: "EYFS (UK Early Years)",           ageMin: 4,  ageMax: 5,
    flag: "🇬🇧", chip: "UK EYFS",     short: "UK Early Years · Ages 3–5",
    description: "For nursery & reception kids in the UK. Learning through play, phonics, counting to 10." },
  { id: "uk-ks1",    label: "UK National Curriculum · KS1",    ageMin: 5,  ageMax: 7,
    flag: "🇬🇧", chip: "UK KS1",      short: "UK · Ages 5–7 (Year 1–2)",
    description: "England's standard for Year 1 & 2. Phonics, times tables to 10, world continents." },
  { id: "uk-ks2",    label: "UK National Curriculum · KS2",    ageMin: 7,  ageMax: 11,
    flag: "🇬🇧", chip: "UK KS2",      short: "UK · Ages 7–11 (Year 3–6)",
    description: "England's standard for Year 3–6. Multiplication, fractions, rivers, Romans." },
  { id: "uk-ks3",    label: "UK National Curriculum · KS3",    ageMin: 11, ageMax: 14,
    flag: "🇬🇧", chip: "UK KS3",      short: "UK · Ages 11–14 (Year 7–9)",
    description: "England's early secondary. Algebra, physics, world history and geography." },
  { id: "ib-pyp",    label: "IB PYP (Primary Years)",          ageMin: 3,  ageMax: 12,
    flag: "🌐", chip: "IB PYP",       short: "International Baccalaureate · Ages 3–12",
    description: "Inquiry-led, used in international schools worldwide. Themed units across all subjects." },
  { id: "ib-myp",    label: "IB MYP (Middle Years)",           ageMin: 11, ageMax: 15,
    flag: "🌐", chip: "IB MYP",       short: "International Baccalaureate · Ages 11–15",
    description: "IB's middle-school programme with global contexts and interdisciplinary learning." },
  { id: "cam-prim",  label: "Cambridge Primary",               ageMin: 5,  ageMax: 11,
    flag: "🎓", chip: "Cambridge",    short: "Cambridge Primary · Ages 5–11",
    description: "Cambridge International's primary curriculum, used in 160+ countries." },
  { id: "cam-lsec",  label: "Cambridge Lower Secondary",       ageMin: 11, ageMax: 14,
    flag: "🎓", chip: "Cam. L-Sec",   short: "Cambridge Lower Secondary · Ages 11–14",
    description: "Cambridge International's lower-secondary programme, preparing for IGCSE." },
  // UAE MOE entry removed from user-facing lists — pending compliance
  // review (UAE CDS Law + PDPL). Data still exists in /lib/syllabus.js
  // so re-enabling later is a one-line change.
  { id: "common-core", label: "Common Core (USA K-8)",         ageMin: 5,  ageMax: 14,
    flag: "🇺🇸", chip: "US CC",       short: "US Common Core · Ages 5–14 (K–8)",
    description: "The US Common Core State Standards for maths and English, K through Grade 8." },
  { id: "other",     label: "Other / Not sure",                ageMin: 4,  ageMax: 15,
    flag: "🤷", chip: "Other",       short: "Not sure yet",
    description: "Pick this if you're homeschooling flexibly — we'll show a broad mix of topics." },
];

/** Compact chip label + flag emoji per curriculum id. */
export const CURRICULUM_CHIP = Object.fromEntries(
  CURRICULA.map((c) => [c.id, { chip: c.chip || c.label, flag: c.flag || "" }]),
);

/**
 * Which curricula are meaningful for a given age.
 * Returns the subset of curriculum ids whose ageMin/ageMax overlaps the age.
 * Used to hide filter chips that will always be empty at that age.
 */
export function curriculaForAge(age) {
  if (!age) return CURRICULA.map((c) => c.id);
  return CURRICULA
    .filter((c) => age >= (c.ageMin - 1) && age <= (c.ageMax + 1))
    .map((c) => c.id);
}

/** Subject buckets shown as checkboxes. */
export const SUBJECTS = [
  { id: "geography", label: "Geography",       emoji: "🌍" },
  { id: "maths",     label: "Maths",           emoji: "➗" },
  { id: "science",   label: "Science",         emoji: "🔬" },
  { id: "english",   label: "English & Reading", emoji: "📚" },
  { id: "history",   label: "History",         emoji: "🏛️" },
  { id: "nature",    label: "Nature",          emoji: "🌿" },
  { id: "space",     label: "Space",           emoji: "🚀" },
  { id: "languages", label: "World Languages", emoji: "🗣️" },
  { id: "art",       label: "Art & Design",    emoji: "🎨" },
  { id: "ict",       label: "ICT & Coding",    emoji: "💻" },
  { id: "pe",        label: "PE & Health",     emoji: "⚽" },
  { id: "re",        label: "World Religions", emoji: "🕊️" },
  { id: "pshe",      label: "Wellbeing (PSHE)", emoji: "💛" },
  { id: "creative",  label: "Creative Play",   emoji: "✨" },
];

/** Daily time options → target minutes per day. */
export const TIME_OPTIONS = [
  { id: "15", label: "15 minutes", minutes: 15 },
  { id: "30", label: "30 minutes", minutes: 30 },
  { id: "45", label: "45 minutes", minutes: 45 },
  { id: "60", label: "1 hour",     minutes: 60 },
];

/**
 * Learning-module library. Each entry maps to an existing route in the app
 * so the daily checklist is always tappable. `minutes` are approximate,
 * `standards` are the *curriculum-level* tags this activity practises,
 * `outcomes` are fine-grained syllabus-outcome codes from /lib/syllabus.js
 * (used by the Term Report to cite specific learning objectives),
 * `ages` is the target range. Keep this list wide-ish so plans can rotate.
 */
export const MODULE_LIBRARY = [
  // ── Geography ────────────────────────────────────────────────────
  { id: "learn-countries", subject: "geography", title: "Explore 3 Countries", route: "/learn",
    minutes: 15, ages: [4,15],
    standards: ["eyfs","uk-ks1","uk-ks2","ib-pyp","cam-prim","uae-moe","common-core"],
    outcomes: ["uk-ks1-geo-1","uk-ks2-geo-1","ib-pyp-geo-1","cam-prim-geo-1","uae-moe-ss-1","cc-ss-1"],
    outcome: "Names 3 countries and their continents." },
  { id: "continent-maps",  subject: "geography", title: "Continent Map Tour", route: "/continents",
    minutes: 15, ages: [5,12],
    standards: ["uk-ks1","uk-ks2","ib-pyp","cam-prim","uae-moe","common-core"],
    outcomes: ["uk-ks1-geo-1","uk-ks2-geo-1","ib-pyp-geo-1","uae-moe-ss-1","cc-ss-1"],
    outcome: "Locates all 7 continents on a world map." },
  { id: "globe-3d",        subject: "geography", title: "Spin the 3D Globe", route: "/globe",
    minutes: 10, ages: [4,15],
    standards: ["eyfs","uk-ks1","uk-ks2","uk-ks3","ib-pyp","ib-myp","cam-prim","cam-lsec","uae-moe","common-core"],
    outcomes: ["uk-ks1-geo-1","uk-ks1-geo-3","uk-ks2-geo-3","ib-pyp-geo-1","cc-ss-1"],
    outcome: "Explores continents and oceans on a rotating globe." },
  { id: "passport",        subject: "geography", title: "Stamp Your Passport", route: "/passport",
    minutes: 10, ages: [5,15],
    standards: ["uk-ks1","uk-ks2","ib-pyp","cam-prim","uae-moe"],
    outcomes: ["uk-ks1-geo-1","uk-ks2-geo-1","ib-pyp-geo-1","uae-moe-ss-1"],
    outcome: "Records countries visited with flags and facts." },
  { id: "quiz-geo",        subject: "geography", title: "Geography Quiz (5 Q)", route: "/quiz",
    minutes: 10, ages: [6,15],
    standards: ["uk-ks1","uk-ks2","uk-ks3","ib-pyp","ib-myp","cam-prim","cam-lsec","uae-moe","common-core"],
    outcomes: ["uk-ks1-geo-1","uk-ks2-geo-1","uk-ks2-geo-4","ib-pyp-geo-1","cam-prim-geo-1","uae-moe-ss-1","cc-ss-1"],
    outcome: "Answers 5 questions on flags, capitals or continents." },

  // ── Maths ────────────────────────────────────────────────────────
  { id: "maths-academy",   subject: "maths",     title: "Maths Academy Level", route: "/maths-academy",
    minutes: 20, ages: [5,13],
    standards: ["uk-ks1","uk-ks2","uk-ks3","ib-pyp","cam-prim","cam-lsec","uae-moe","common-core"],
    outcomes: ["uk-ks1-ma-1","uk-ks1-ma-2","uk-ks2-ma-1","uk-ks2-ma-2","ib-pyp-ma-1","cam-prim-ma-1","cc-ma-1","cc-ma-2"],
    outcome: "Completes one adaptive level in the current year group." },
  { id: "math-snap",       subject: "maths",     title: "Math Snap Speed Round", route: "/snap",
    minutes: 10, ages: [6,12],
    standards: ["uk-ks1","uk-ks2","ib-pyp","cam-prim","uae-moe","common-core"],
    outcomes: ["uk-ks1-ma-2","uk-ks2-ma-2","ib-pyp-ma-1","cc-ma-1"],
    outcome: "Practises mental arithmetic for fluency." },
  { id: "math-geo",        subject: "maths",     title: "Math Geography", route: "/math",
    minutes: 15, ages: [7,12],
    standards: ["uk-ks2","ib-pyp","cam-prim","common-core"],
    outcomes: ["uk-ks2-ma-4","uk-ks2-ma-5","ib-pyp-ma-3","cam-prim-ma-3"],
    outcome: "Solves word problems with a geography twist." },

  // ── Science ──────────────────────────────────────────────────────
  { id: "know-science",    subject: "science",   title: "Science Lab Card", route: "/hub/science",
    minutes: 15, ages: [6,14],
    standards: ["uk-ks1","uk-ks2","uk-ks3","ib-pyp","ib-myp","cam-prim","cam-lsec","common-core"],
    outcomes: ["uk-ks1-sc-3","uk-ks2-sc-3","uk-ks2-sc-4","uk-ks3-sc-2","ib-pyp-sc-3","cam-prim-sc-2","cc-sc-2"],
    outcome: "Reads 1 science card and answers a comprehension prompt." },
  { id: "know-space",      subject: "space",     title: "Space Cadets Reading", route: "/hub/space",
    minutes: 15, ages: [6,14],
    standards: ["uk-ks1","uk-ks2","uk-ks3","ib-pyp","ib-myp","cam-prim","common-core"],
    outcomes: ["uk-ks2-sc-1","uk-ks3-sc-3","ib-pyp-sc-2","cam-prim-sc-2","cc-sc-3"],
    outcome: "Learns about a planet, star or space mission." },

  // ── Nature ───────────────────────────────────────────────────────
  { id: "know-nature",     subject: "nature",    title: "Nature Trail Discovery", route: "/hub/nature",
    minutes: 15, ages: [4,12],
    standards: ["eyfs","uk-ks1","uk-ks2","ib-pyp","cam-prim","uae-moe","common-core"],
    outcomes: ["eyfs-und-world","uk-ks1-sc-1","uk-ks2-sc-2","ib-pyp-sc-1","cam-prim-sc-1","cc-sc-1"],
    outcome: "Meets 1 new animal or ecosystem." },
  { id: "explore-animals", subject: "nature",    title: "Explore Animals", route: "/explore/animals",
    minutes: 10, ages: [4,10],
    standards: ["eyfs","uk-ks1","ib-pyp","cam-prim","uae-moe"],
    outcomes: ["uk-ks1-sc-1","ib-pyp-sc-1","cam-prim-sc-1"],
    outcome: "Identifies 3 animals and their habitats." },

  // ── English & Reading (NEW) ──────────────────────────────────────
  { id: "story-facts",     subject: "english",   title: "Country Fact Reader", route: "/learn",
    minutes: 10, ages: [5,12],
    standards: ["uk-ks1","uk-ks2","ib-pyp","cam-prim","common-core"],
    outcomes: ["uk-ks1-en-1","uk-ks2-en-1","uk-ks2-en-2","cc-ela-1"],
    outcome: "Reads a short country fact card and answers 1 question." },
  { id: "blog-explorer",   subject: "english",   title: "Explorer Blog Story", route: "/blog",
    minutes: 15, ages: [7,13],
    standards: ["uk-ks2","ib-pyp","common-core"],
    outcomes: ["uk-ks2-en-1","uk-ks2-en-2","cc-ela-1"],
    outcome: "Reads a blog post and retells 2 key ideas." },

  // ── History (NEW) ────────────────────────────────────────────────
  { id: "wonders-history", subject: "history",   title: "Wonders of the World", route: "/hub/wonders",
    minutes: 15, ages: [7,14],
    standards: ["uk-ks2","ib-pyp","cam-prim","common-core"],
    outcomes: ["uk-ks2-hi-1","uk-ks2-hi-2","uk-ks2-hi-3","cc-ss-2"],
    outcome: "Learns about 1 ancient civilisation or wonder." },
  { id: "cultures-hist",   subject: "history",   title: "World Cultures Card", route: "/explore/cultures",
    minutes: 10, ages: [6,12],
    standards: ["uk-ks1","uk-ks2","ib-pyp","cam-prim","uae-moe","common-core"],
    outcomes: ["uk-ks1-hi-2","uk-ks2-hi-3","ib-pyp-geo-3","uae-moe-ss-2","cc-ss-2"],
    outcome: "Meets a new culture, celebration or tradition." },

  // ── Languages ────────────────────────────────────────────────────
  { id: "world-words",     subject: "languages", title: "World Words Phrase Pack", route: "/languages",
    minutes: 10, ages: [5,15],
    standards: ["uk-ks1","uk-ks2","uk-ks3","ib-pyp","ib-myp","cam-prim","cam-lsec","uae-moe"],
    outcomes: ["uk-ks2-la-1","uk-ks2-la-2","ib-pyp-la-1","uae-moe-ar-1"],
    outcome: "Learns 3-5 phrases in a new language with audio." },

  // ── ICT & Coding (NEW) ───────────────────────────────────────────
  { id: "coding-quiz",     subject: "ict",       title: "Coding Concepts Quiz", route: "/quiz?mode=coding",
    minutes: 15, ages: [8,14],
    standards: ["uk-ks2","uk-ks3","ib-myp","cam-lsec","common-core"],
    outcomes: ["uk-ks2-ma-5","uk-ks3-ma-1","cam-lsec-ma-1"],
    outcome: "Answers 5 questions on algorithms and logic." },

  // ── Art & Creative ───────────────────────────────────────────────
  { id: "coloring",        subject: "art",       title: "Coloring Page", route: "/coloring",
    minutes: 20, ages: [4,10],
    standards: ["eyfs","uk-ks1","ib-pyp","uae-moe"],
    outcomes: ["eyfs-und-world","uk-ks1-art-1"],
    outcome: "Completes a flag or landmark coloring page." },
  { id: "puzzles",         subject: "creative",  title: "Country Puzzle", route: "/puzzles",
    minutes: 15, ages: [5,12],
    standards: ["uk-ks1","uk-ks2","ib-pyp","cam-prim","uae-moe"],
    outcomes: ["uk-ks1-geo-3","uk-ks2-geo-1","ib-pyp-geo-1"],
    outcome: "Solves a country / landmark shape puzzle." },
  { id: "printables",      subject: "creative",  title: "Printable Worksheet", route: "/printables",
    minutes: 20, ages: [4,10],
    standards: ["eyfs","uk-ks1","ib-pyp","cam-prim","uae-moe","common-core"],
    outcomes: ["uk-ks1-geo-3","uk-ks1-art-1","ib-pyp-geo-1"],
    outcome: "Completes an off-screen printable worksheet." },
];

/** Curriculum labels keyed by id — used in report/tag chips. */
export const CURRICULUM_LABEL = Object.fromEntries(CURRICULA.map((c) => [c.id, c.label]));

/** Subject labels keyed by id — used in schedule row tags. */
export const SUBJECT_LABEL = Object.fromEntries(SUBJECTS.map((s) => [s.id, s.label]));

/** Feb 2026 · Curriculum-id → LessonBrowser "framework" LABEL map.
 *  Lesson records tag their curriculum by human label (e.g. "UK NC",
 *  "Cambridge Primary") — not by the id we use elsewhere (`uk-ks2`,
 *  `cam-prim`). This map bridges the two so /my-learning + Lesson
 *  Library can scope by the child's chosen curriculum. */
export const CURRICULUM_ID_TO_FRAMEWORK = {
  "eyfs":         "UK NC",
  "uk-ks1":       "UK NC",
  "uk-ks2":       "UK NC",
  "uk-ks3":       "UK NC",
  "ib-pyp":       "IB PYP",
  "ib-myp":       "IB MYP",
  "cam-prim":     "Cambridge Primary",
  "cam-lsec":     "Cambridge Lower Secondary",
  "common-core":  "Common Core",
  // `other` intentionally maps to null — signals "don't filter".
  "other":        null,
};

/** Convenience: returns the framework label for a curriculum id, or
 *  null if none applies (e.g. "other" / unknown). Used by
 *  LessonBrowser + SubjectCatalog to scope to a child's curriculum. */
export function curriculumFramework(curriculumId) {
  if (!curriculumId) return null;
  return CURRICULUM_ID_TO_FRAMEWORK[curriculumId] || null;
}

/** Feb 2026 · Framework family map — the LessonBrowser chip labels
 *  are short ("UK NC") but the lesson `curriculum` field often holds
 *  the full canonical name ("UK National Curriculum · KS2"). This
 *  map lets the client filter against ANY variant so no framework
 *  chip ever returns near-zero when the data exists.
 *
 *  Values are the STRING SET a lesson's `curriculum` or entries in
 *  `curricula[]` may contain. Matching is case-sensitive substring
 *  on any element in the set — Mongo docs use the exact separators
 *  below.
 */
export const FRAMEWORK_FAMILY = {
  "UK NC": [
    "UK NC", "UK National Curriculum",
    // Alternate en-dash / hyphen separators in the seed data.
    "UK National Curriculum · KS", "UK National Curriculum — KS",
    "UK KS", "UK EYFS", "UK PSHE",
  ],
  "IB PYP":                    ["IB PYP", "IB Primary Years Programme"],
  "IB MYP":                    ["IB MYP", "IB Middle Years Programme"],
  "Cambridge Primary":         ["Cambridge Primary"],
  "Cambridge Lower Secondary": ["Cambridge Lower Secondary"],
  "CBSE":                      ["CBSE"],
  "UAE MoE":                   ["UAE MoE"],
  "Common Core":               ["Common Core"],
  "NGSS":                      ["NGSS", "US NGSS"],
  "CEFR-aligned":              ["CEFR"],
  "CSTA-aligned":              ["CSTA"],
};

/** True if a lesson matches the framework chip label. Used by the
 *  LessonBrowser filter and the SubjectCatalog client-side scope. */
export function lessonMatchesFramework(lesson, framework) {
  if (!framework || framework === "All") return true;
  const family = FRAMEWORK_FAMILY[framework] || [framework];
  const curriculumStr = String(lesson.curriculum || "");
  const curriculaArr = Array.isArray(lesson.curricula) ? lesson.curricula : [];
  return family.some((needle) => {
    if (curriculumStr.includes(needle)) return true;
    return curriculaArr.some((c) => String(c || "").includes(needle));
  });
}

